import { AxiosResponse } from 'axios'
import { inject, InjectionKey, reactive, ref } from 'vue'
import { Illust, Message, RoomSummary } from '../../../libs/models'
import { useApi } from '../libs/api'
import { ILLUSTS_PER_PAGE, MESSAGES_PER_PAGE } from '../../../libs/constants'

export interface RoomStore {
  readonly value: RoomSummary | null
  init: (roomId: number, code?: string) => void
  fetch: () => Promise<RoomSummary | null>
  fetchOldIllusts: () => Promise<void>
  fetchOldMessages: () => Promise<void>
}
export const generateRoomStore = (): RoomStore => {
  const room = ref<RoomSummary | null>(null)
  const completedIllusts = ref(false)
  const completedMessages = ref(false)
  const api = useApi()
  const config = reactive<{ roomId?: number, code?: string }>({ roomId: undefined, code: undefined })
  const init = (roomId: number, code?: string) => {
    room.value = null
    completedIllusts.value = false
    completedMessages.value = false
    config.roomId = roomId
    config.code = code
  }
  const fetch = (): Promise<RoomSummary | null> => {
    if (!config.roomId) throw new Error('Not initialized')
    const params: any = { code: config.code }
    // TODO: イラストのfetch数10件くらいにする
    // if (room.value?.illusts.length) params['illusts[after]'] = room.value.illusts[0].id // to update stamps
    if (room.value?.messages.length) params['messages[after]'] = room.value.messages[0].id
    return api.get(`/rooms/${config.roomId}`, { params }).then(({ data }: AxiosResponse<RoomSummary>) => {
      if (config.roomId !== data.id) return room.value
      if (!room.value) {
        room.value = data
        completedIllusts.value = data.illusts.length < ILLUSTS_PER_PAGE
        completedMessages.value = data.messages.length < MESSAGES_PER_PAGE
      } else {
        room.value.users = data.users
        const oldestId = data.illusts[data.illusts.length - 1]?.id || 0
        room.value.illusts.splice(0, room.value.illusts.findIndex(v => v.id === oldestId) + 1, ...data.illusts)
        room.value.messages.splice(0, room.value.messages.findIndex(v => v.id <= params['messages[after]']), ...data.messages)
      }
      return room.value
    })
  }
  const fetchOldIllusts = async () => {
    if (completedIllusts.value) return
    if (!config.roomId) throw new Error('Not initialized')
    const roomId = config.roomId
    const params: any = { code: config.code }
    if (room.value?.illusts.length) params.before = room.value.illusts[room.value.illusts.length - 1].id
    return api.get(`/rooms/${roomId}/illusts`, { params }).then(({ data: illusts }: AxiosResponse<Illust[]>) => {
      if (roomId !== config.roomId) return
      room.value?.illusts.push(...illusts)
      completedIllusts.value = illusts.length < ILLUSTS_PER_PAGE
    })
  }
  const fetchOldMessages = async () => {
    if (completedMessages.value) return
    if (!config.roomId) throw new Error('Not initialized')
    const roomId = config.roomId
    const params: any = { code: config.code }
    if (room.value?.messages.length) params.before = room.value.messages[room.value.messages.length - 1].id
    return api.get(`/rooms/${roomId}/messages`, { params }).then(({ data: messages }: AxiosResponse<Message[]>) => {
      if (roomId !== config.roomId) return
      room.value?.messages.push(...messages)
      completedMessages.value = messages.length < MESSAGES_PER_PAGE
    })
  }
  return {
    get value () {
      return room.value
    },
    init,
    fetch,
    fetchOldIllusts,
    fetchOldMessages
  }
}
export const roomStoreKey: InjectionKey<RoomStore> = Symbol()
export const useRoomStore = (): RoomStore => {
  const roomStore = inject(roomStoreKey)
  if (!roomStore) throw Error('Could not inject roomStore')
  return roomStore
}
